import { ArrowSquareOut } from '@phosphor-icons/react';
import { useAuthStore } from '@/auth/store';
import { RatingBadge } from '@/ui/RatingBadge';
import { useRatings, type LichessCadence } from '@/api/ratings';

/**
 * LichessTab — onglet Lichess détaillé (T6 P1 Stats Hub).
 *
 * 6 cards perfs (Classique / Rapide / Blitz / Bullet / Puzzle / PuzzleRush).
 * Ordre hiérarchique cadences : Classique → Rapide → Blitz (Bullet caché par
 * défaut, ajouté via "Voir bullet"). Puzzle + PuzzleRush en queue car
 * conceptuellement distincts du jeu.
 *
 * Refresh manuel : la sync passe par le cron horaire backend MVP2.5 ; pas
 * de bouton "Sync maintenant" exposé ici (pas de mutation endpoint dispo
 * sans MVP4+).
 */

const LICHESS_PRIMARY: LichessCadence[] = ['classical', 'rapid', 'blitz', 'puzzle'];
const LICHESS_HIDDEN: LichessCadence[] = ['bullet', 'puzzleRush'];

export function LichessTab(): JSX.Element {
  const ratings = useRatings();
  const lichessUsername = useAuthStore((s) => s.user?.lichess_username) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <section>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div>
            <h2 className="text-heading-3 text-paper-50 m-0">
              <span style={{ color: 'var(--violet-500)' }}>Lichess</span>
              <span className="text-ink-300 text-body ml-2 font-mono uppercase tracking-wider">
                entraînement
              </span>
            </h2>
            <p className="text-body-sm text-ink-300 italic mt-1">
              Mis à jour automatiquement (cron horaire).
            </p>
          </div>
          {lichessUsername !== null && lichessUsername !== '' && (
            <a
              href={`https://lichess.org/@/${lichessUsername}/perf/rapid`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-ink-700 text-paper-50 text-body-sm hover:border-violet-500 transition-colors"
            >
              Profil Lichess
              <ArrowSquareOut size={14} weight="regular" />
            </a>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {LICHESS_PRIMARY.map((cad) => {
            const r = ratings.data?.lichess?.[cad] ?? null;
            return (
              <RatingBadge
                key={cad}
                platform="lichess"
                cadence={cad}
                value={r?.value ?? null}
                updatedAt={r?.updated_at ?? null}
                variant="card"
              />
            );
          })}
        </div>
      </section>

      <details className="rounded-xl border border-ink-700 bg-ink-800 p-4">
        <summary className="text-caption uppercase tracking-widest text-ink-300 cursor-pointer hover:text-paper-50">
          Voir bullet et puzzle rush
        </summary>
        <div className="grid grid-cols-2 gap-3 mt-3">
          {LICHESS_HIDDEN.map((cad) => {
            const r = ratings.data?.lichess?.[cad] ?? null;
            return (
              <RatingBadge
                key={cad}
                platform="lichess"
                cadence={cad}
                value={r?.value ?? null}
                updatedAt={r?.updated_at ?? null}
                variant="card"
              />
            );
          })}
        </div>
      </details>
    </div>
  );
}
